/**
 * The DevTools admin pages as sub-pages of the DevTools page of
 * `@backstage/plugin-devtools/alpha`: each one is a tab of `/devtools`.
 *
 * @packageDocumentation
 */

import {
  createFrontendModule,
  SubPageBlueprint,
} from '@backstage/frontend-plugin-api';
import devtoolsPlugin from '@backstage/plugin-devtools/alpha';
import { devToolsTaskSchedulerReadPermission } from '@backstage/plugin-devtools-common/alpha';
import { devtoolsAdminReadPermission } from '@internal/plugin-permissions-common';

/**
 * Only for users the permission policy allows `devtools.admin.read`: the
 * app checks the predicate after sign-in and leaves the tab out otherwise.
 */
const adminOnly = {
  permissions: { $contains: devtoolsAdminReadPermission.name },
};

/** The catalog health checks at `/devtools/catalog-health` */
export const catalogHealthSubPage = SubPageBlueprint.make({
  name: 'catalog-health',
  attachTo: { id: 'page:devtools', input: 'pages' },
  if: adminOnly,
  params: {
    path: 'catalog-health',
    title: 'Catalog Health',
    loader: async () => {
      const [{ CatalogHealthContent }, { SubPageContent }] = await Promise.all([
        import('./components/CatalogHealthContent/CatalogHealthContent'),
        import('./components/shared/SubPageContent'),
      ]);
      return (
        <SubPageContent permission={devtoolsAdminReadPermission}>
          <CatalogHealthContent />
        </SubPageContent>
      );
    },
  },
});

/** The scaffolder insights at `/devtools/scaffolder` */
export const scaffolderSubPage = SubPageBlueprint.make({
  name: 'scaffolder',
  attachTo: { id: 'page:devtools', input: 'pages' },
  if: adminOnly,
  params: {
    path: 'scaffolder',
    title: 'Scaffolder',
    loader: async () => {
      const [{ ScaffolderInsightsContent }, { SubPageContent }] =
        await Promise.all([
          import('./components/ScaffolderInsightsContent/ScaffolderInsightsContent'),
          import('./components/shared/SubPageContent'),
        ]);
      return (
        <SubPageContent permission={devtoolsAdminReadPermission}>
          <ScaffolderInsightsContent />
        </SubPageContent>
      );
    },
  },
});

/** The TechDocs insights at `/devtools/techdocs` */
export const techdocsSubPage = SubPageBlueprint.make({
  name: 'techdocs',
  attachTo: { id: 'page:devtools', input: 'pages' },
  if: adminOnly,
  params: {
    path: 'techdocs',
    title: 'TechDocs',
    loader: async () => {
      const [{ TechDocsInsightsContent }, { SubPageContent }] =
        await Promise.all([
          import('./components/TechDocsInsightsContent/TechDocsInsightsContent'),
          import('./components/shared/SubPageContent'),
        ]);
      return (
        <SubPageContent permission={devtoolsAdminReadPermission}>
          <TechDocsInsightsContent />
        </SubPageContent>
      );
    },
  },
});

/**
 * The External Dependencies tab at `/devtools/external-dependencies`: the
 * integration checks (reachability, GitHub rate limits and App coverage,
 * expiries) and the endpoints of `devTools.externalDependencies`. Admins
 * only; the endpoints need `devtools.external-dependencies` too.
 */
export const externalDependenciesSubPage = SubPageBlueprint.make({
  name: 'external-dependencies',
  attachTo: { id: 'page:devtools', input: 'pages' },
  if: adminOnly,
  params: {
    path: 'external-dependencies',
    title: 'External Dependencies',
    loader: async () => {
      const [{ IntegrationsContent }, { SubPageContent }] = await Promise.all([
        import('./components/IntegrationsContent/IntegrationsContent'),
        import('./components/shared/SubPageContent'),
      ]);
      return (
        <SubPageContent permission={devtoolsAdminReadPermission}>
          <IntegrationsContent />
        </SubPageContent>
      );
    },
  },
});

/**
 * The permission debugger and ownership view at `/devtools/permissions`, for
 * support requests like "I cannot see, edit or delete X"
 */
export const permissionsSubPage = SubPageBlueprint.make({
  name: 'permissions',
  attachTo: { id: 'page:devtools', input: 'pages' },
  if: adminOnly,
  params: {
    path: 'permissions',
    title: 'Permissions',
    loader: async () => {
      const [{ PermissionsContent }, { SubPageContent }] = await Promise.all([
        import('./components/PermissionsContent/PermissionsContent'),
        import('./components/shared/SubPageContent'),
      ]);
      return (
        <SubPageContent permission={devtoolsAdminReadPermission}>
          <PermissionsContent />
        </SubPageContent>
      );
    },
  },
});

/**
 * The Scheduled Tasks tab of the DevTools plugin, only for users with
 * `devtools.scheduler.read`. It lists the tasks of the plugins in
 * `devTools.scheduledTasks.plugins`.
 */
export const scheduledTasksSubPage = devtoolsPlugin
  .getExtension('sub-page:devtools/scheduled-tasks')
  .override({
    if: {
      permissions: { $contains: devToolsTaskSchedulerReadPermission.name },
    },
  });

/**
 * Module for the DevTools plugin: install it next to
 * `@backstage/plugin-devtools/alpha`. It adds the admin tabs to `/devtools` and
 * shows Scheduled Tasks to admins only.
 */
export const devtoolsAdminModule = createFrontendModule({
  pluginId: 'devtools',
  extensions: [
    scheduledTasksSubPage,
    externalDependenciesSubPage,
    catalogHealthSubPage,
    scaffolderSubPage,
    techdocsSubPage,
    permissionsSubPage,
  ],
});
